import React, { useEffect, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  CircularProgress,
} from "@mui/material";
import { useUnit } from "effector-react";
import {
  getVehiclesEffect,
  getVehiclesByClientEffect,
  getVehiclesByFleetEffect,
} from "../../stores/VehiclesStore/VehiclesStore.effects";
import { VehiclesStore } from "../../stores/VehiclesStore/VehiclesStore";
import { CustomersStore } from "../../stores/CustomersStore/CustomersStore";
import { FleetsStore } from "../../stores/FleetsStore/FleetsStore";
import {
  getCustomersEffect,
  getWashingTypesEffect,
} from "../../stores/CustomersStore/CustomersStore.effects";
import { getFleetsEffect } from "../../stores/FleetsStore/FleetsStore.effects";
import { PageContainer, PageHeader, PageTitle } from "../../components/PageLayout";
import { NotFound } from "./VehiclesStyles";
import AddVehicle from "./components/AddVehicle/AddVehicle";
import VehicleRow from "./components/VehicleRow/VehicleRow";

const Vehicles: React.FC = () => {
  const { vehicles } = useUnit(VehiclesStore);
  const { customers } = useUnit(CustomersStore);
  const { fleets } = useUnit(FleetsStore);
  const loadingAll = useUnit(getVehiclesEffect.pending);
  const loadingByClient = useUnit(getVehiclesByClientEffect.pending);
  const loadingByFleet = useUnit(getVehiclesByFleetEffect.pending);
  const loading = loadingAll || loadingByClient || loadingByFleet;

  const [selectedClientId, setSelectedClientId] = useState("");
  const [selectedFleetId, setSelectedFleetId] = useState("");

  useEffect(() => {
    getVehiclesEffect();
    getCustomersEffect();
    getWashingTypesEffect();
    getFleetsEffect();
  }, []);

  const availableFleets = selectedClientId
    ? fleets.filter((f) => f.client?.id === selectedClientId)
    : fleets;

  const handleClientFilter = (clientId: string) => {
    setSelectedClientId(clientId);
    setSelectedFleetId("");
    if (clientId) {
      getVehiclesByClientEffect(clientId);
    } else {
      getVehiclesEffect();
    }
  };

  const handleFleetFilter = (fleetId: string) => {
    setSelectedFleetId(fleetId);
    if (fleetId) {
      getVehiclesByFleetEffect(fleetId);
    } else if (selectedClientId) {
      getVehiclesByClientEffect(selectedClientId);
    } else {
      getVehiclesEffect();
    }
  };

  const renderVehicles = () => {
    if (loading && !vehicles?.length)
      return (
        <tr>
          <NotFound colSpan={4}>
            <CircularProgress size={28} />
          </NotFound>
        </tr>
      );

    if (!vehicles?.length)
      return (
        <tr>
          <NotFound colSpan={4}>Sem veículos para exibir</NotFound>
        </tr>
      );
    return vehicles.map((vehicle) => (
      <VehicleRow key={vehicle.id} vehicle={vehicle} />
    ));
  };

  return (
    <PageContainer style={{ height: "100%", boxSizing: "border-box", overflow: "hidden" }}>
      <PageHeader>
        <PageTitle>Veículos</PageTitle>
        <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
          <FormControl size="small" sx={{ minWidth: 220, backgroundColor: "#fff" }}>
            <InputLabel>Filtrar por cliente</InputLabel>
            <Select
              value={selectedClientId}
              label="Filtrar por cliente"
              onChange={(e) => handleClientFilter(e.target.value)}
            >
              <MenuItem value="">Todos</MenuItem>
              {customers.map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220, backgroundColor: "#fff" }}>
            <InputLabel>Filtrar por frota</InputLabel>
            <Select
              value={selectedFleetId}
              label="Filtrar por frota"
              onChange={(e) => handleFleetFilter(e.target.value)}
            >
              <MenuItem value="">Todas</MenuItem>
              {availableFleets.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.description}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <AddVehicle />
        </div>
      </PageHeader>

      <TableContainer sx={{ flex: 1, minHeight: 0, overflow: "auto" }} component={Paper}>
        <Table stickyHeader sx={{ minWidth: 650 }} aria-label="vehicles table">
          <TableHead>
            <TableRow>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Placa</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Cliente</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }}>Frota</TableCell>
              <TableCell sx={{ whiteSpace: "nowrap" }} align="right">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{renderVehicles()}</TableBody>
        </Table>
      </TableContainer>
    </PageContainer>
  );
};

export default Vehicles;
